import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, Panel, Button, IconButton, Tag, Banner, EmptyState, Skeleton, Spinner, Dialog, Stepper, ChoiceCards,
  Field, TextInput, Select, Checkbox, ToneIcon, Icon, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatFetchedAt } from './ivrTypeMeta.js';
import {
  EXTENSION_TYPES, fieldsForTypeId, resolveTypeIdFromValue, typeValueForId, isSensitivePath, displayPath, diffFields, wereFieldsRemoved,
} from './ivrExtensionFieldSchemas.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { Row, KV } from '../../next/ui';
import IvrFilePreviewDialog from './IvrFilePreviewDialog.jsx';
import { useScreenSettings } from '../../next/related/relatedSettingsStore';

/**
 * IVR — ימות המשיח, new design. Same endpoints as IvrExplorer:
 *   /ivr/status, /ivr/dir, /ivr/extension, POST|PUT|DELETE /ivr/extensions, /ivr/backups (+ restore).
 * Tiles of the extensions at the current level, breadcrumbs, a details panel (ext.ini + files),
 * a step-by-step extension editor, delete with typed path + backup, the backups list, and a preview of any file.
 */
const SYSTEM_LABEL = process.env.REACT_APP_SYSTEM_LABEL || 'מערכת בחינות';

/** type → label / tone / ck icon (same buckets as ivrTypeMeta) */
export function ivrTypeTone(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('api')) return { label: 'API', tone: 'primary', icon: 'code' };
  if (t.includes('menu') || t === 'id_list') return { label: 'תפריט', tone: 'primary', icon: 'list' };
  if (t.includes('play') || t.includes('last_play') || t === 'file') return { label: 'השמעה', tone: 'info', icon: 'play' };
  if (t.includes('record') || t.includes('voicemail')) return { label: 'הקלטה', tone: 'violet', icon: 'mic' };
  if (t.includes('queue') || t.includes('routing') || t.includes('conference')) return { label: 'ניתוב', tone: 'warning', icon: 'swap' };
  if (t.includes('exam') || t.includes('trivia') || t.includes('points')) return { label: 'מבחן', tone: 'success', icon: 'task' };
  if (t.includes('turbo')) return { label: 'טורבו', tone: 'danger', icon: 'sparkle' };
  if (!t) return { label: 'שלוחה', tone: 'neutral', icon: 'folder' };
  return { label: type, tone: 'neutral', icon: 'help' };
}

const fileMeta = (f) => [
  f.durationStr,
  f.size != null ? (f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(f.size / 1024))}KB`) : '',
  f.mtime,
].filter(Boolean).join(' · ');

const pathSegments = (path) => {
  const p = String(path || '').replace(/^\/+/, '').replace(/\/+$/, '');
  return p ? p.split('/').filter(Boolean) : [];
};

export default function IvrExplorerNext() {
  const toast = useCkToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('ivr', 'create');
  const canUpdate = hasPermission('ivr', 'update');
  const canDelete = hasPermission('ivr', 'delete');
  const canRead = hasPermission('ivr', 'read');

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [path, setPath] = useState('');
  const [dirData, setDirData] = useState(null);
  const [dirLoading, setDirLoading] = useState(true);
  const [dirError, setDirError] = useState('');
  const [extension, setExtension] = useState(null);
  const [extLoading, setExtLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [backupsOpen, setBackupsOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const segments = useMemo(() => pathSegments(path), [path]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get('/ivr/status');
      setStatus(data);
    } catch (err) {
      setStatus({ connected: false, error: err.response?.data?.error || err.message || 'שגיאה בטעינת סטטוס', systemLabel: SYSTEM_LABEL, fetchedAt: new Date().toISOString() });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadDir = useCallback(async (nextPath) => {
    setDirLoading(true);
    setDirError('');
    setExtension(null);
    try {
      const { data } = await api.get('/ivr/dir', { params: { path: nextPath || '' } });
      setDirData(data);
      setPath(data.path || '');
    } catch (err) {
      setDirError(err.response?.data?.error || err.message || 'שגיאה בטעינת שלוחות');
      setDirData(null);
    } finally {
      setDirLoading(false);
    }
  }, []);

  const loadExtension = useCallback(async (extPath) => {
    if (!extPath) { setExtension(null); return; }
    setExtLoading(true);
    try {
      const { data } = await api.get('/ivr/extension', { params: { path: extPath } });
      setExtension(data);
    } catch {
      setExtension(null);
    } finally {
      setExtLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); loadDir(''); }, [loadStatus, loadDir]);
  useEffect(() => { if (path) loadExtension(path); else setExtension(null); }, [path, loadExtension]);

  const refresh = () => { loadStatus(); loadDir(path); };
  const openDir = (dir) => loadDir(dir.path || [...segments, dir.name].join('/'));
  const goToSegment = (i) => (i < 0 ? loadDir('') : loadDir(segments.slice(0, i + 1).join('/')));
  const openCreate = () => { setEditorMode('create'); setEditorOpen(true); };
  const openEdit = () => { setEditorMode('edit'); setEditorOpen(true); };

  useScreenSettings([
    canRead ? { key: 'ivr-backups', title: 'גיבויי שלוחות', description: 'שחזור הגדרות של שלוחות שנמחקו', icon: 'history', onClick: () => setBackupsOpen(true) } : null,
  ]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editorMode === 'create') {
        const body = { parentPath: payload.parentPath || '', name: payload.name, fields: payload.fields };
        if (payload.confirmPath) body.confirmPath = payload.confirmPath;
        const { data } = await api.post('/ivr/extensions', body);
        toast.success(data.message || 'השלוחה נוצרה');
        setEditorOpen(false);
        await loadDir(path);
      } else {
        const body = { path: payload.path, fields: payload.fields, mode: payload.mode || 'merge' };
        if (payload.confirmPath) body.confirmPath = payload.confirmPath;
        const { data } = await api.put('/ivr/extensions', body);
        toast.success(data.message || 'השלוחה עודכנה');
        setEditorOpen(false);
        await loadDir(path);
        await loadExtension(path);
      }
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'שמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async ({ path: delPath, confirmPath, acknowledgeFiles }) => {
    setDeleting(true);
    try {
      const { data } = await api.delete('/ivr/extensions', { params: { path: delPath }, data: { confirmPath, acknowledgeFiles } });
      toast.success(data.backup?.id ? `${data.message || 'נמחק'} · גיבוי נשמר` : (data.message || 'השלוחה נמחקה'));
      setDeleteOpen(false);
      await loadDir(pathSegments(delPath).slice(0, -1).join('/'));
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'מחיקה נכשלה');
    } finally {
      setDeleting(false);
    }
  };

  const childCount = extension?.dir?.dirs?.length ?? dirData?.counts?.dirs ?? 0;
  const fileCount = ((extension?.dir?.files?.length ?? dirData?.files?.length) || 0) + ((extension?.dir?.ini?.length ?? dirData?.ini?.length) || 0);
  const connected = status?.connected;
  const fields = extension?.ini?.fields || {};
  const fieldEntries = Object.entries(fields).slice(0, 40);
  const files = [...(dirData?.ini || []), ...(dirData?.html || []), ...(dirData?.files || [])];

  return (
    <div className="nx-stack">
      <PageHeader
        title="IVR — ימות המשיח"
        sub={[status?.systemLabel || SYSTEM_LABEL, status?.session?.username || null, status?.fetchedAt ? `עודכן ${formatFetchedAt(status.fetchedAt)}` : null].filter(Boolean).join(' · ')}
        primary={canCreate ? { label: 'שלוחה חדשה', onClick: openCreate } : undefined}
        more={[
          { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: refresh },
          canRead ? { key: 'backups', label: 'גיבויי שלוחות', icon: 'history', onClick: () => setBackupsOpen(true) } : null,
        ].filter(Boolean)}
      >
        {statusLoading && !status ? <Spinner size={18} /> : (
          <Tag tone={connected ? 'success' : 'danger'} icon={connected ? 'check' : 'alert'}>{connected ? 'מחובר לימות' : 'לא מחובר'}</Tag>
        )}
        {status?.stats?.dirs != null ? <Tag>{`${status.stats.dirs} שלוחות`}</Tag> : null}
        {status?.stats?.files != null ? <Tag>{`${status.stats.files} קבצים`}</Tag> : null}
      </PageHeader>

      {status && !status.connected && status.error ? <Banner tone="warning" icon="alert">{status.error}</Banner> : null}

      <div className="ck-panel" style={{ padding: '8px 12px' }}>
        <nav className="nx-crumbs" aria-label="נתיב שלוחות">
          {segments.length ? <button type="button" onClick={() => goToSegment(-1)}><Icon name="home" size={14} /> ראשי</button>
            : <span className="is-last"><Icon name="home" size={14} /> ראשי</span>}
          {segments.map((seg, i) => (
            <React.Fragment key={`${seg}-${i}`}>
              <Icon name="left" size={13} />
              {i === segments.length - 1 ? <span className="is-last" dir="ltr">{seg}</span> : <button type="button" dir="ltr" onClick={() => goToSegment(i)}>{seg}</button>}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {dirError ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={() => loadDir(path)}>נסו שוב</Button>}>{dirError}</Banner> : null}

      <div className="nx-ivr-layout">
        <Panel
          title="שלוחות"
          sub={dirLoading ? 'טוען…' : `${(dirData?.dirs || []).length} ברמה זו · לחצו על שלוחה לפתיחה`}
          icon="phone"
          actions={canCreate ? <Button size="sm" icon="plus" disabled={!connected} onClick={openCreate}>שלוחה חדשה</Button> : null}
        >
          {dirLoading ? (
            <div className="nx-tiles">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={92} />)}</div>
          ) : (dirData?.dirs || []).length === 0 ? (
            <EmptyState
              icon="folder"
              title="אין שלוחות ברמה זו"
              action={(
                <Row justify="center">
                  {canCreate ? <Button variant="primary" icon="plus" onClick={openCreate}>שלוחה חדשה</Button> : null}
                  {segments.length ? <Button onClick={() => goToSegment(segments.length - 2)}>חזרה לרמה הקודמת</Button> : null}
                </Row>
              )}
            >
              {canCreate ? 'ניתן ליצור שלוחה חדשה כאן, או לחזור לרמה הקודמת.' : 'ניתן לעיין בקבצים בפאנל הפרטים, או לחזור לרמה הקודמת.'}
            </EmptyState>
          ) : (
            <div className="nx-tiles">
              {dirData.dirs.map((d) => {
                const meta = ivrTypeTone(d.type);
                return (
                  <button key={d.path || d.name} type="button" className="nx-tile" onClick={() => openDir(d)}>
                    <span className="nx-tile__top">
                      <ToneIcon icon={meta.icon} tone={meta.tone} size={34} />
                      <span className="nx-tile__num">{d.name}</span>
                      <span className="ck-grow" />
                      <Icon name="left" size={15} />
                    </span>
                    <span>{d.title || <span className="ck-muted">ללא כותרת</span>}</span>
                    <span className="nx-item__tags">
                      <Tag tone={meta.tone}>{meta.label}</Tag>
                      <span className="nx-code">{d.displayPath || `/${d.path}`}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel
          title="פרטי הרמה"
          icon="gear"
          actions={<span className="nx-code">{path ? `/${path}` : '/'}</span>}
        >
          <div className="nx-stack">
            <div className="nx-item__tags">
              <Tag icon="folder">{`${dirData?.counts?.dirs ?? 0} שלוחות`}</Tag>
              <Tag icon="file">{`${dirData?.counts?.files ?? 0} קבצים`}</Tag>
              <Tag>{`${dirData?.counts?.ini ?? 0} ini`}</Tag>
            </div>
            {path && (canUpdate || canDelete) ? (
              <Row>
                {canUpdate ? <Button size="sm" icon="edit" disabled={extLoading} onClick={openEdit}>עריכת הגדרות</Button> : null}
                {canDelete ? <Button size="sm" variant="danger" icon="trash" disabled={extLoading} onClick={() => setDeleteOpen(true)}>מחיקה</Button> : null}
              </Row>
            ) : null}
            {!path ? <Banner tone="info" icon="info">זו הרמה הראשית. בחרו שלוחה מהרשימה או צרו שלוחה חדשה.</Banner> : null}
            {extLoading ? <div className="nx-stack"><Skeleton height={24} /><Skeleton height={24} /><Skeleton height={70} /></div> : null}
            {!extLoading && extension?.ini ? (
              <div className="nx-stack" style={{ gap: 8 }}>
                <h3 className="nx-sub-h">הגדרות שלוחה (ext.ini)</h3>
                {(extension.type || extension.title) ? (
                  <div className="nx-item__tags">
                    {extension.type ? <Tag tone="primary">{`type=${extension.type}`}</Tag> : null}
                    {extension.title ? <Tag>{extension.title}</Tag> : null}
                  </div>
                ) : null}
                {fieldEntries.length ? (
                  <div style={{ maxHeight: 260, overflow: 'auto' }}>
                    <KV items={fieldEntries.map(([k, v]) => ({ label: k, value: String(v), ltr: /https?:|ivr2:|\//i.test(String(v)) }))} />
                  </div>
                ) : <span className="ck-muted">אין שדות ב-ini</span>}
              </div>
            ) : null}
            {!extLoading && !extension?.ini && path ? (
              <Banner tone="info" icon="info">לא נמצא קובץ הגדרות (ext.ini) לשלוחה זו — אפשר ליצור הגדרות דרך «עריכת הגדרות».</Banner>
            ) : null}
            <hr className="nx-hr" />
            <h3 className="nx-sub-h">קבצים ברמה זו — לחצו על קובץ לתצוגה</h3>
            {files.length ? (
              <div className="nx-list" style={{ maxHeight: 260, overflow: 'auto' }}>
                {files.map((f) => (
                  <button
                    key={`${f.kind}-${f.path || f.name}`}
                    type="button"
                    className="nx-item"
                    style={{ padding: '8px 10px', width: '100%', textAlign: 'start', cursor: 'pointer', background: 'none', border: 0, font: 'inherit', color: 'inherit' }}
                    onClick={() => setPreviewFile(f)}
                  >
                    <Icon name={f.fileType === 'AUDIO' ? 'play' : 'file'} size={16} />
                    <div className="nx-item__main"><b>{f.name}</b><small dir="ltr" style={{ textAlign: 'start' }}>{fileMeta(f) || f.displayPath || f.path}</small></div>
                    <Tag>{f.name.split('.').pop()}</Tag>
                  </button>
                ))}
              </div>
            ) : <span className="ck-muted">אין קבצים ברמה זו</span>}
          </div>
        </Panel>
      </div>

      <IvrFilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
      <ExtensionEditorDialog
        open={editorOpen}
        mode={editorMode}
        parentPath={path}
        extension={editorMode === 'edit' ? extension : null}
        saving={saving}
        onClose={() => (saving ? null : setEditorOpen(false))}
        onSave={handleSave}
      />
      <DeleteExtensionDialog
        open={deleteOpen}
        path={path}
        title={extension?.title || ''}
        childCount={childCount}
        fileCount={fileCount}
        deleting={deleting}
        onClose={() => (deleting ? null : setDeleteOpen(false))}
        onConfirm={handleDelete}
      />
      <BackupsDialog
        open={backupsOpen}
        canRestore={canCreate}
        onClose={() => setBackupsOpen(false)}
        onRestored={() => { refresh(); toast.success('הגדרות השלוחה שוחזרו מהגיבוי'); }}
      />
    </div>
  );
}

/* ── עורך שלוחה (שלבים) ─────────────────────────────────────────── */
const STEPS_CREATE = ['סוג השלוחה', 'פרטים', 'סיכום'];
const STEPS_EDIT = ['הגדרות', 'סיכום'];

function ExtensionEditorDialog({ open, mode = 'create', parentPath = '', extension = null, saving = false, onClose, onSave }) {
  const isCreate = mode === 'create';
  const steps = isCreate ? STEPS_CREATE : STEPS_EDIT;
  const [activeStep, setActiveStep] = useState(0);
  const initialFields = useMemo(() => extension?.ini?.fields || {}, [extension]);
  const [typeId, setTypeId] = useState('menu');
  const [customType, setCustomType] = useState('');
  const [name, setName] = useState('');
  const [form, setForm] = useState({});
  const [advanced, setAdvanced] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmPath, setConfirmPath] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setError('');
    setConfirmPath('');
    setShowAdvanced(false);
    if (isCreate) {
      setTypeId('menu');
      setCustomType('');
      setName('');
      setForm({ title: '', enter_id: 'no' });
      setAdvanced([]);
    } else {
      const fields = { ...initialFields };
      const tid = resolveTypeIdFromValue(fields.type);
      setTypeId(tid);
      setCustomType(tid === 'other' ? String(fields.type || '') : '');
      setName(extension?.path?.split('/').pop() || '');
      const guided = fieldsForTypeId(tid);
      const guidedKeys = new Set(guided.map((f) => f.key));
      const nextForm = {};
      guided.forEach((f) => { nextForm[f.key] = fields[f.key] ?? (f.key === 'enter_id' ? 'no' : ''); });
      setForm(nextForm);
      setAdvanced(Object.entries(fields).filter(([k]) => k !== 'type' && !guidedKeys.has(k)).map(([key, value]) => ({ key, value: String(value ?? '') })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, extension?.path]);

  const guidedSchema = useMemo(() => fieldsForTypeId(typeId), [typeId]);
  const resolvedType = typeValueForId(typeId, customType);
  const composedFields = useMemo(() => {
    const out = { ...form };
    if (resolvedType) out.type = resolvedType;
    advanced.forEach((row) => { const k = String(row.key || '').trim(); if (k) out[k] = String(row.value ?? ''); });
    return out;
  }, [form, advanced, resolvedType]);
  const targetPath = useMemo(() => {
    if (!isCreate) return extension?.path || '';
    const parent = String(parentPath || '').replace(/^\/+|\/+$/g, '');
    const seg = String(name || '').trim();
    if (!seg) return parent;
    return parent ? `${parent}/${seg}` : seg;
  }, [isCreate, parentPath, name, extension?.path]);
  const needsConfirm = isSensitivePath(targetPath);
  const changes = useMemo(() => (isCreate ? diffFields({}, composedFields) : diffFields(initialFields, composedFields)), [isCreate, initialFields, composedFields]);
  const needReplace = !isCreate && wereFieldsRemoved(initialFields, composedFields);
  const typeChanged = !isCreate && String(initialFields.type || '') !== String(composedFields.type || '');
  const setFormKey = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const detailsStep = (isCreate && activeStep === 1) || (!isCreate && activeStep === 0);

  const validateStep = () => {
    setError('');
    if (isCreate && activeStep === 0) {
      if (typeId === 'other' && !customType.trim()) { setError('יש להזין את סוג השלוחה (type)'); return false; }
      if (typeId !== 'other' && !resolvedType) { setError('יש לבחור סוג שלוחה'); return false; }
    }
    if (detailsStep) {
      if (isCreate) {
        if (!String(name).trim()) { setError('יש להזין מספר או שם לשלוחה החדשה'); return false; }
        if (/[\\/]/.test(name)) { setError('השם לא יכול להכיל קו נטוי'); return false; }
      }
      const missing = guidedSchema.find((f) => f.required && !String(form[f.key] || '').trim());
      if (missing) { setError(`חסר: ${missing.label}`); return false; }
      if (typeId === 'other' && !customType.trim()) { setError('חסר סוג השלוחה'); return false; }
    }
    if (activeStep === steps.length - 1) {
      if (needsConfirm && confirmPath.trim().replace(/^\/+/, '') !== String(targetPath).replace(/^\/+/, '')) { setError(`נתיב רגיש — הקלידו בדיוק: ${displayPath(targetPath)}`); return false; }
      if (changes.length === 0 && !isCreate) { setError('לא בוצע שינוי'); return false; }
    }
    return true;
  };
  const next = () => { if (validateStep()) setActiveStep((s) => Math.min(s + 1, steps.length - 1)); };
  const back = () => { setError(''); setActiveStep((s) => Math.max(s - 1, 0)); };
  const submit = () => {
    if (!validateStep()) return;
    onSave?.({ parentPath, name: String(name).trim(), path: targetPath, fields: composedFields, mode: needReplace ? 'replace' : 'merge', confirmPath: needsConfirm ? String(targetPath) : undefined });
  };
  const onTypePick = (id) => {
    setTypeId(id);
    setForm((prev) => {
      const out = { ...prev };
      fieldsForTypeId(id).forEach((f) => { if (out[f.key] == null) out[f.key] = f.key === 'enter_id' ? 'no' : ''; });
      return out;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      title={isCreate ? 'שלוחה חדשה' : `עריכת שלוחה ${displayPath(extension?.path)}`}
      icon={isCreate ? 'plus' : 'edit'}
      size="md"
      footer={(
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>ביטול</Button>
          <span className="ck-grow" />
          {activeStep > 0 ? <Button disabled={saving} onClick={back}>הקודם</Button> : null}
          {activeStep < steps.length - 1
            ? <Button variant="primary" onClick={next}>המשך</Button>
            : <Button variant="primary" icon="check" disabled={saving} onClick={submit}>{saving ? 'שומר…' : isCreate ? 'צור שלוחה' : 'שמור שינויים'}</Button>}
        </>
      )}
    >
      <div className="nx-stack">
        <Stepper steps={steps} active={activeStep} />
        {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}

        {isCreate && activeStep === 0 ? (
          <>
            <p className="nx-desc">בחרו מה השלוחה עושה — אחר כך נמלא רק את מה שצריך.</p>
            <ChoiceCards
              columns={2}
              value={typeId}
              onChange={onTypePick}
              ariaLabel="סוג השלוחה"
              options={EXTENSION_TYPES.map((t) => ({ value: t.id, label: t.label, description: t.description, icon: ivrTypeTone(t.typeValue || 'other').icon }))}
            />
            {typeId === 'other' ? (
              <Field label="סוג טכני (type)" hint="למשל: queue, conference, id_list">
                <TextInput ltr value={customType} onChange={(e) => setCustomType(e.target.value)} />
              </Field>
            ) : null}
          </>
        ) : null}

        {detailsStep ? (
          <>
            {!isCreate ? (
              <div className="nx-item__tags">
                <Tag>{`type=${initialFields.type || '—'}`}</Tag>
                {typeChanged ? <Tag tone="warning">סוג השלוחה ישתנה</Tag> : null}
              </div>
            ) : null}
            {isCreate ? (
              <Field label="מספר או שם השלוחה *" hint={`תיווצר תחת ${displayPath(parentPath)} כ־${displayPath(targetPath || '…')}`}>
                <TextInput ltr value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
              </Field>
            ) : (
              <Field label="סוג השלוחה" hint="שינוי סוג עלול לשנות את התנהגות הקו">
                <Select value={typeId} onChange={onTypePick} options={EXTENSION_TYPES.map((t) => ({ value: t.id, label: t.label }))} />
              </Field>
            )}
            {!isCreate && typeId === 'other' ? (
              <Field label="סוג טכני (type)"><TextInput ltr value={customType} onChange={(e) => setCustomType(e.target.value)} /></Field>
            ) : null}
            {guidedSchema.map((f) => (
              <Field key={f.key} label={`${f.label}${f.required ? ' *' : ''}`} hint={f.help}>
                {f.input === 'select'
                  ? <Select value={form[f.key] ?? ''} onChange={(v) => setFormKey(f.key, v)} options={(f.options || []).map((o) => ({ value: o.value, label: o.label }))} />
                  : <TextInput value={form[f.key] ?? ''} dir={f.dir || 'rtl'} onChange={(e) => setFormKey(f.key, e.target.value)} />}
              </Field>
            ))}
            <div className="ck-panel">
              <button type="button" className="ck-panel__head ck-panel__toggle" aria-expanded={showAdvanced} onClick={() => setShowAdvanced((v) => !v)} style={{ width: '100%' }}>
                <span className="ck-panel__titles"><b>מצב מתקדם</b><small>שדות חופשיים כמו ב-ext.ini — רק אם אתם יודעים מה לעשות</small></span>
                <Icon name={showAdvanced ? 'up' : 'down'} size={15} />
              </button>
              {showAdvanced ? (
                <div className="ck-panel__body nx-stack" style={{ gap: 8 }}>
                  {advanced.map((row, idx) => (
                    <Row key={idx} wrap={false}>
                      <TextInput ltr placeholder="מפתח" style={{ width: 150 }} value={row.key} onChange={(e) => { const n = [...advanced]; n[idx] = { ...n[idx], key: e.target.value }; setAdvanced(n); }} />
                      <TextInput ltr placeholder="ערך" value={row.value} onChange={(e) => { const n = [...advanced]; n[idx] = { ...n[idx], value: e.target.value }; setAdvanced(n); }} />
                      <IconButton icon="trash" label="הסרת שדה" onClick={() => setAdvanced(advanced.filter((_, i) => i !== idx))} />
                    </Row>
                  ))}
                  <div><Button size="sm" icon="plus" onClick={() => setAdvanced([...advanced, { key: '', value: '' }])}>הוספת שדה</Button></div>
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        {activeStep === steps.length - 1 ? (
          <>
            <Banner tone="info" icon="info">{isCreate ? `תיווצר שלוחה חדשה בנתיב ${displayPath(targetPath)}` : `יעודכנו הגדרות השלוחה ${displayPath(targetPath)}`}</Banner>
            {typeChanged ? <Banner tone="warning" icon="alert">{`סוג השלוחה משתנה מ־«${initialFields.type || 'ללא'}» ל־«${composedFields.type || 'ללא'}». זה עלול לשנות את התנהגות הקו למתקשרים.`}</Banner> : null}
            {needReplace ? <Banner tone="warning" icon="alert">חלק מהשדות הוסרו — יישמר קובץ הגדרות מלא מחדש (לא רק עדכון חלקי).</Banner> : null}
            <h3 className="nx-sub-h">מה ישתנה</h3>
            {changes.length ? (
              <div className="nx-list" style={{ maxHeight: 240, overflow: 'auto' }}>
                {changes.map((c) => (
                  <div key={c.key} className="nx-item" style={{ padding: '7px 10px' }}>
                    <span className="nx-code">{c.key}</span>
                    {c.kind === 'add' ? <span style={{ color: 'var(--ck-success)' }}>{`← חדש: ${c.after || '(ריק)'}`}</span> : null}
                    {c.kind === 'remove' ? <span style={{ color: 'var(--ck-danger)' }}>{`← יוסר (${c.before})`}</span> : null}
                    {c.kind === 'change' ? <span className="ck-muted">{`${c.before || '(ריק)'} → ${c.after || '(ריק)'}`}</span> : null}
                  </div>
                ))}
              </div>
            ) : <span className="ck-muted">אין שינויים</span>}
            {needsConfirm ? (
              <>
                <Banner tone="warning" icon="key">{`זה נתיב חשוב במערכת. לאישור — הקלידו את הנתיב המדויק: ${displayPath(targetPath)}`}</Banner>
                <Field label="אישור נתיב"><TextInput ltr value={confirmPath} placeholder={displayPath(targetPath)} onChange={(e) => setConfirmPath(e.target.value)} /></Field>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ── מחיקת שלוחה ─────────────────────────────────────────────────── */
function DeleteExtensionDialog({ open, path = '', title = '', childCount = 0, fileCount = 0, deleting = false, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [ackFiles, setAckFiles] = useState(false);
  const expected = String(path || '').replace(/^\/+/, '');
  const shown = displayPath(path);
  const blocked = childCount > 0;
  const sensitive = isSensitivePath(path);
  useEffect(() => { if (open) { setTyped(''); setAckFiles(false); } }, [open, path]);
  const canSubmit = !blocked && !deleting && typed.trim().replace(/^\/+/, '') === expected && (fileCount === 0 || ackFiles);
  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      title={`מחיקת שלוחה ${shown}`}
      icon="trash"
      size="sm"
      footer={(
        <>
          <Button variant="ghost" disabled={deleting} onClick={onClose}>ביטול</Button>
          <Button variant="danger-solid" disabled={!canSubmit} onClick={() => onConfirm?.({ path, confirmPath: expected, acknowledgeFiles: fileCount > 0 })}>
            {deleting ? 'מוחק…' : 'מחק עם גיבוי'}
          </Button>
        </>
      )}
    >
      <div className="nx-stack">
        {title ? <p className="nx-desc">{`כותרת: ${title}`}</p> : null}
        {blocked ? (
          <Banner tone="danger" icon="alert">{`לא ניתן למחוק — יש ${childCount} תת-שלוחות בתוך ${shown}. היכנסו פנימה ומחקו קודם את תת-השלוחות, ואז חזרו לכאן.`}</Banner>
        ) : (
          <>
            <Banner tone="warning" icon="history">לפני המחיקה יישמר <b>גיבוי של הגדרות השלוחה</b> (קובץ ההגדרות). אפשר לשחזר את ההגדרות אחר כך ממסך הגיבויים.</Banner>
            <Banner tone="info" icon="info">קבצי שמע ותוכן אחר בתוך השלוחה <b>יימחקו ולא ישוחזרו אוטומטית</b>. הגיבוי שומר את ההגדרות בלבד.</Banner>
            {fileCount > 0 ? <Checkbox checked={ackFiles} onChange={setAckFiles} label={`אני מבין/ה ש-${fileCount} קבצים בשלוחה יימחקו לצמיתות`} /> : null}
            {sensitive ? <Banner tone="warning" icon="key">זה נתיב חשוב במערכת הבחינות. המחיקה תשפיע על מתקשרים.</Banner> : null}
            <Field label={<>לאישור — הקלידו את הנתיב המדויק: <b dir="ltr">{shown}</b></>}>
              <TextInput ltr value={typed} placeholder={shown} disabled={deleting} onChange={(e) => setTyped(e.target.value)} data-autofocus />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

/* ── גיבויים ─────────────────────────────────────────────────────── */
function BackupsDialog({ open, filterPath = '', canRestore = false, onClose, onRestored }) {
  const toast = useCkToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmPath, setConfirmPath] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (filterPath) params.path = filterPath;
      const { data } = await api.get('/ivr/backups', { params });
      setItems(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'שגיאה בטעינת גיבויים');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterPath]);
  useEffect(() => { if (open) { setConfirmFor(null); setConfirmPath(''); load(); } }, [open, load]);

  const doRestore = async () => {
    if (!confirmFor) return;
    const p = confirmFor.path;
    if (isSensitivePath(p) && confirmPath.trim().replace(/^\/+/, '') !== String(p).replace(/^\/+/, '')) {
      toast.error(`נתיב רגיש — הקלידו: ${displayPath(p)}`);
      return;
    }
    setRestoringId(confirmFor.id);
    try {
      const body = isSensitivePath(p) ? { confirmPath: p } : {};
      const { data } = await api.post(`/ivr/backups/${confirmFor.id}/restore`, body);
      toast.success(data.note || 'ההגדרות שוחזרו');
      setConfirmFor(null);
      await load();
      onRestored?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'שחזור נכשל');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="גיבויי שלוחות"
      icon="history"
      size="lg"
      footer={(
        <>
          <Button variant="ghost" icon="refresh" disabled={loading} onClick={load}>רענון</Button>
          <span className="ck-grow" />
          <Button onClick={onClose}>סגירה</Button>
        </>
      )}
    >
      <div className="nx-stack">
        <Banner tone="info" icon="info">הגיבוי שומר את <b>הגדרות השלוחה</b> (ext.ini). קבצי שמע לא נשמרים כאן — שחזור מחזיר הגדרות בלבד.</Banner>
        {filterPath ? <p className="nx-desc">{`מסונן לנתיב: ${displayPath(filterPath)}`}</p> : null}
        {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
        {confirmFor ? (
          <div className="ck-panel" style={{ padding: 12 }}>
            <div className="nx-stack">
              <Banner tone="warning" icon="history">
                שחזור הגדרות לנתיב <b dir="ltr">{confirmFor.displayPath || displayPath(confirmFor.path)}</b>. אם השלוחה לא קיימת — תיווצר מחדש עם ההגדרות השמורות.
              </Banner>
              {isSensitivePath(confirmFor.path) ? (
                <Field label={`הקלידו לאישור: ${displayPath(confirmFor.path)}`}><TextInput ltr value={confirmPath} onChange={(e) => setConfirmPath(e.target.value)} /></Field>
              ) : null}
              <Row>
                <Button variant="primary" icon="history" disabled={!!restoringId} onClick={doRestore}>{restoringId ? 'משחזר…' : 'אשר שחזור'}</Button>
                <Button variant="ghost" disabled={!!restoringId} onClick={() => setConfirmFor(null)}>ביטול</Button>
              </Row>
            </div>
          </div>
        ) : null}
        {loading ? <div className="ck-empty"><Spinner size={24} /></div> : items.length === 0 ? (
          <EmptyState compact icon="history" title="אין גיבויים עדיין" />
        ) : (
          <div className="nx-list" style={{ maxHeight: 440, overflow: 'auto' }}>
            {items.map((b) => (
              <div key={b.id} className="nx-item">
                <div className="nx-item__main">
                  <b dir="ltr" style={{ textAlign: 'start' }}>{b.displayPath || displayPath(b.path)}</b>
                  <small>{[formatHebrewDateTime(b.createdAt) || b.createdAt, b.createdBy, b.title].filter(Boolean).join(' · ')}</small>
                  <div className="nx-item__tags">
                    {b.type ? <Tag>{b.type}</Tag> : null}
                    {b.restoredAt ? <Tag tone="success">{`שוחזר ${formatHebrewDateTime(b.restoredAt)}`}</Tag> : null}
                    {b.fileCount > 0 ? <Tag>{`${b.fileCount} קבצים במידע`}</Tag> : null}
                  </div>
                </div>
                {canRestore ? <Button size="sm" icon="history" disabled={!!restoringId} onClick={() => { setConfirmFor(b); setConfirmPath(''); }}>שחזר הגדרות</Button> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
